import { useEffect, useRef, useState } from "react";

import { Business, Person } from "@material-ui/icons";
import { Button, Input, Modal, Select } from "antd";
import styled from "styled-components";

import { DASHBOARD_TYPES, DashboardType } from "models/workspace";

interface NewDashboardInputBase {
  // eslint-disable-next-line no-unused-vars
  onAdd: (title: string, type?: DashboardType) => void;
  onCancel: () => void;
  isLoading: boolean;
  error: string;
  visible: boolean;
  title: string;
  dashboardTitle: string;
  setTitle?: boolean;
  addButtonLabel?: string;
}

/**
 * Showing the type picker and supplying the types it may offer are one decision, so the
 * two travel together — the caller owns that rule, since which types are creatable
 * depends on the workspace as well as the user's role. Expressed as a union rather than
 * two optional props so `setType` without `typeOptions` cannot compile into an empty
 * select whose Save submits no type.
 */
export type NewDashboardInputInputModel = NewDashboardInputBase &
  (
    | { setType: true; typeOptions: DashboardType[] }
    | { setType?: false; typeOptions?: never }
  );

// System is never creatable, but the map is total so a new DashboardType cannot be
// added without deciding on an icon for it.
const TYPE_ICONS: Record<DashboardType, JSX.Element> = {
  Personal: <Person />,
  Organization: <Business />,
  System: <Business />
};

export default function NewDashboardInput(input: NewDashboardInputInputModel) {
  const [title, setTitle] = useState(input.dashboardTitle);
  const [selectedType, setSelectedType] = useState<DashboardType>(
    DASHBOARD_TYPES.Personal
  );
  const titleRef = useRef(null);

  const typeOptions = input.typeOptions ?? [];

  // Derived rather than stored so the dialog can never submit a type the caller no
  // longer allows — the workspace behind that list can change while this is open.
  const type = typeOptions.includes(selectedType) ? selectedType : typeOptions[0];

  // Re-suggest the title whenever the caller's suggestion changes, which it does while
  // the dialog is open as the workspace's dashboard count moves.
  useEffect(() => {
    if (input.visible) {
      setTitle(input.dashboardTitle);
      if (titleRef.current) {
        titleRef.current.select();
      }
    }
  }, [input.visible, input.dashboardTitle]);

  // Reset the type on open only. The dialog stays mounted between opens (`visible` only
  // drives antd's `open`), so a type picked and then cancelled would otherwise become
  // the next open's default and carry org-wide visibility with it. Keyed on `visible`
  // alone so a title re-suggestion mid-dialog cannot discard a deliberate pick.
  // Personal is the least-visible option; where it is not offered the derived `type`
  // above coerces this to the first one that is.
  useEffect(() => {
    if (input.visible) {
      setSelectedType(DASHBOARD_TYPES.Personal);
    }
  }, [input.visible]);

  const handleCancel = () => {
    input.onCancel();
  };

  const includeTitle = input.setTitle === undefined || input.setTitle;
  const includeType = input.setType;

  return (
    <Modal
      open={input.visible}
      onOk={handleCancel}
      onCancel={handleCancel}
      closable={false}
      title={<NewDashboardPopoverTitle>{input.title}</NewDashboardPopoverTitle>}
      wrapClassName="copy-dashboard-modal-root"
      footer={[
        <Button key="cancel" onClick={input.onCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          disabled={input.isLoading}
          loading={input.isLoading}
          onClick={() => {
            input.onAdd(title, type);
          }}>
          {input.addButtonLabel ?? "Save"}
        </Button>
      ]}>
      <CopyDashboardContainer>
        <Options>
          {includeTitle && (
            <OptionItem>
              <Label>Dashboard Title</Label>
              <Input
                ref={titleRef}
                value={title}
                placeholder="Enter the new dashboards name"
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </OptionItem>
          )}
          {includeType && (
            <OptionItem>
              <Label>Dashboard Type</Label>
              <SelectWrapper
                popupClassName="modal-select"
                value={type}
                onChange={(value: DashboardType) => {
                  setSelectedType(value);
                }}
                options={typeOptions.map((option) => ({
                  label: (
                    <DashboardTypeItem>
                      {TYPE_ICONS[option]} {option}
                    </DashboardTypeItem>
                  ),
                  value: option
                }))}
              />
            </OptionItem>
          )}
        </Options>

        {input.error !== "" && <ErrorContainer>{input.error}</ErrorContainer>}
      </CopyDashboardContainer>
    </Modal>
  );
}

const CopyDashboardContainer = styled.div``;

const Label = styled.label`
  min-width: 120px;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  .ant-card-body {
    padding: 5px;
    height: 100%;
    flex-direction: row;
    justify-content: space-around;
  }
`;
const OptionItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  gap: 5px;
  .ant-slider {
    width: 80px;
  }

  .ant-select {
    width: 100px;
  }
  .ant-btn {
    border-radius: 5px;
  }
  .ant-btn-circle {
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .ant-btn:hover {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }
  padding: 2px 0;
`;

const DashboardTypeItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const SelectWrapper = styled(Select)`
  width: 240px !important;
`;

const NewDashboardPopoverTitle = styled.p`
  font-weight: var(--fontWeightBold);
`;

const ErrorContainer = styled.div`
  display: inline-flex;
  color: var(--color-danger);
  font-weight: var(--fontWeightMedium);
`;
